import { Col, Divider, message, Row, Select, Skeleton, Statistic } from "antd";
import React, { useEffect, useState } from "react";
import { getAllResourceRequestsData } from "../../services/Apis";
import ResourcesTable from "../components/ResourceRequestsTable";
import LogoutPopUp from "../../components/LogoutPopUp";
import ServiceLineSummaryByRegion from "../components/ServiceLineSummaryByRegion";
import { UserOutlined } from "@ant-design/icons";

const ResourceRequest: React.FC = () => {
  const [tokenExpired, setTokenExpired] = useState<boolean>(false);

  const hasAccess = JSON.parse(
    localStorage.getItem("user_details") as string
  ).features.includes("AUDIT_RESOURCE_CHANGES");

  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [allResourcesData, setAllResourcesData] = useState<any[]>([]);
  const [filteredResourcesData, setFilteredResourcesData] = useState<any[]>([]);

  const [serviceLineSummary, setServiceLineSummary] = useState<any>({});

  const [selectedRegion, setSelectedRegion] = useState<string[]>([]);
  const [selectedClient, setSelectedClient] = useState<string[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<string[]>([]);

  const [regionOptions, setRegionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [clientOptions, setClientOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [dealOptions, setDealOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [serviceLineFilter, setServiceLineFilter] = useState<any>({});

  const [totalCost, setTotalCost] = useState<number>(0);

  const getUnique = (key: string, filteredData: any[]) => {
    const values = filteredData.map((deal) => deal[key]);
    const options = Array.from(new Set(values));
    return options.map((option) => ({
      value: option,
      label: option,
    }));
  };

  const getFilteredData = (exclude: "region" | "client" | "deal") => {
    return allResourcesData.filter((deal) => {
      return (
        (exclude === "region" ||
          selectedRegion.length === 0 ||
          selectedRegion.includes(deal.region_code)) &&
        (exclude === "client" ||
          selectedClient.length === 0 ||
          selectedClient.includes(deal.client_name)) &&
        (exclude === "deal" ||
          selectedDeal.length === 0 ||
          selectedDeal.includes(deal.deal_name))
      );
    });
  };

  useEffect(() => {
    if(hasAccess) {
    setPageLoading(true);
    getAllResourceRequestsData()
      .then((resp) => {
        setPageLoading(false);
        const errorStatus = (resp as any).error_status ?? (resp as any).status;
        const errorMsg = (resp as any).error ?? (resp as any).data?.error;

        if (errorStatus === 401) {
          setTokenExpired(true);
        }
        if (errorMsg) {
          message.error(errorMsg);
          return;
        }
        if ("data" in resp) {
          setAllResourcesData(resp.data);
          setFilteredResourcesData(resp.data);
          setRegionOptions(getUnique("region_code", resp.data));
          setClientOptions(getUnique("client_name", resp.data));
          setDealOptions(getUnique("deal_name", resp.data));
        }
      })
      .catch((error) => {
        setPageLoading(false);
        message.error("Failed to Get Resource Request Data");
      });
    }
  }, []);

  useEffect(() => {
    setRegionOptions(getUnique("region_code", getFilteredData("region")));
    setClientOptions(getUnique("client_name", getFilteredData("client")));
    setDealOptions(getUnique("deal_name", getFilteredData("deal")));
  }, [allResourcesData, selectedRegion, selectedClient, selectedDeal]);

  useEffect(() => {
    const filteredResourcesData_ = allResourcesData.filter((deal) => {
      return (
        (selectedRegion.length === 0 ||
          selectedRegion.includes(deal.region_code)) &&
        (selectedClient.length === 0 ||
          selectedClient.includes(deal.client_name)) &&
        (selectedDeal.length === 0 || selectedDeal.includes(deal.deal_name))
      );
    });

    const slSummary: any = {};

    for (const record of filteredResourcesData_) {
      const region = record.region_code;
      const slCode = record.service_line_code;
      const revenue = record.total_revenue;

      if (!slSummary[region]) {
        slSummary[region] = {
          total_region_revenue: 0,
          resource_service_lines: {},
          total_region_count: 0
        };
      }

      slSummary[region].total_region_revenue += revenue;

      if (!slSummary[region].resource_service_lines[slCode]) {
        slSummary[region].resource_service_lines[slCode] = {
          count: 0,
          revenue: 0,
        };
      }

      slSummary[region].resource_service_lines[slCode].count += 1;
      slSummary[region].total_region_count += 1
      slSummary[region].resource_service_lines[slCode].revenue += revenue;
    }

    setFilteredResourcesData(filteredResourcesData_);
    setServiceLineSummary(slSummary);
    setServiceLineFilter({})
  }, [allResourcesData, selectedRegion, selectedClient, selectedDeal]);

  useEffect(() => {
    setTotalCost(
      filteredResourcesData
        .reduce((acc, deal) => {
          return acc + deal.total_revenue;
        }, 0)
        .toFixed(0)
    );
  }, [filteredResourcesData]);

  return (
    <>
      {tokenExpired ? (
        <LogoutPopUp openPopup={tokenExpired} />
      ) : (
        <>
          {hasAccess ? (
            <>
              {pageLoading ? (
                <Skeleton
                  active
                  paragraph={{ rows: 8 }}
                  style={{ minHeight: "80vh" }}
                />
              ) : (
                <Row gutter={8}>
                  <Col span={24}>
                    <Divider orientation="left" style={{ marginTop: "-4px" }}>
                      <div style={{ fontWeight: "bold", fontSize: "18px" }}>
                        Resource Requests
                      </div>
                    </Divider>
                  </Col>
                  <Col span={6}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        paddingLeft: "12px",
                        marginTop: "-4px",
                      }}
                    >
                      <CustomSelect
                        label="Client"
                        options={clientOptions}
                        value={selectedClient}
                        onChange={setSelectedClient}
                      />
                      <CustomSelect
                        label="Project"
                        options={dealOptions}
                        value={selectedDeal}
                        onChange={setSelectedDeal}
                      />
                      <Statistic
                        title="Total Revenue"
                        value={totalCost}
                        prefix={"$"}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "8px",
                          padding: "10px",
                          border: "1px solid #f0f0f0",
                          width: "88%",
                          marginTop: "16px",
                        }}
                      />
                      <Statistic
                        title="Total Count"
                        value={filteredResourcesData.length}
                        prefix={<UserOutlined />}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          borderRadius: "8px",
                          padding: "10px",
                          border: "1px solid #f0f0f0",
                          width: "88%",
                          marginTop: "16px",
                        }}
                      />
                    </div>
                  </Col>
                  <Col span={18}>
                    <div
                      style={{
                        display: "flex",
                        gap: "24px",
                        maxWidth:
                          Object.keys(serviceLineSummary).length * 333 + "px",
                        alignItems: "stretch",
                      }}
                    >
                      {Object.keys(serviceLineSummary).map((key) => (
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                          }}
                          key={key}
                        >
                          <ServiceLineSummaryByRegion
                            regionName={key}
                            slData={serviceLineSummary[key]}
                            serviceLineFilter={serviceLineFilter}
                            setServiceLineFilter={setServiceLineFilter}
                          />
                        </div>
                      ))}
                    </div>
                  </Col>

                  <Col span={24}>
                    <Divider />
                  </Col>
                  <Col span={24}>
                    <ResourcesTable
                      filteredResourcesData={filteredResourcesData}
                      serviceLineFilter={serviceLineFilter}
                    />
                  </Col>
                </Row>
              )}{" "}
            </>
          ) : (
            <ResourceRequestHelperInfo />
          )}
        </>
      )}
    </>
  );
};

const CustomSelect = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: any[];
  value: string[];
  onChange: (value: string[]) => void;
}) => {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        width: "100%",
      }}
    >
      <label>{label}</label>
      <Select
        allowClear
        showSearch
        mode="multiple"
        placeholder={"Select " + label}
        optionFilterProp="label"
        onChange={onChange}
        options={options}
        value={value}
        style={{ width: "88%" }}
      />
    </div>
  );
};

export const ResourceRequestHelperInfo = () => {
  return (
    <div style={{ padding: "20px" }}>
      <h3>Resource Change Requests</h3>
      <p>
        Reminder that all resource requests for new deals should be completed
        within the Pricing App until the point ODE is locked.
      </p>
      <p>
        Any request for changes to resources on projects already in-progress,{" "}
        <strong>
          please email{" "}
          <u>
            <a href="mailto:RMO@blend360.com">RMO@blend360.com</a>
          </u>
        </strong>
        &nbsp;with the following information:
      </p>
      <ul>
        <li>
          Client and OpenAir Project Name (unless this is a bench hire not
          immediately tied to client work – please note if so)
        </li>
        <li>
          If resource being removed, include Named Resource, end date, and if
          they're transitioning accounts or exiting Blend
        </li>
        <li>
          If resource being added, include region options, function, level,
          skillset needed along with % allocation and start and end dates; if
          named resource identified, please include
        </li>
      </ul>
      <p>
        For any assistance:{" "}
        <u>
          <a href="mailto:RMO@blend360.com">RMO@blend360.com</a>
        </u>
      </p>
    </div>
  );
};

export default ResourceRequest;
